import { useEffect, useState } from 'react';
import ActiveOrdersPageModel from '../../ActiveOrdersPage/Models/ActiveOrdersPageModel.interface';
import Divider from '../../Atoms/Divider/Divider';
import LoadingCircle from '../../Atoms/Icons/LoadingCircle';
import Dots from '../../Atoms/Loaders/Dots';
import KexLink from '../../Kex/KexLink';
import Accordion from '../../Molecules/Accordion/Accordion';
import OrderDetailsModel from '../../Order/Models/OrderDetailsModel.interface';
import OrderInfoViewModel from '../../Order/Models/OrderInfoViewModel.interface';
import { ApproveOrder, GetOrders } from '../../OrderComponent/Order';
import OrderListing from '../../OrderComponent/OrderListing';
import OrderRowListing from '../../OrderComponent/OrderRowListing';
import { getFormattedDate } from '../../Shared/Common/Helpers';
import useCurrentPage from '../../Shared/Hooks/useCurrentPage';
import { useAppSettingsData } from '../../Shared/Providers/AppSettingsProvider';
import { useTranslationData } from '../../Shared/Providers/TranslationProvider';
import { useUserStateData } from '../../Shared/UserContextProvider/UserContextProvider';
import { styled } from '../../stitches.config';

function ActiveOrdersPage() {
  const {
    staticPages: { orderDetailsPage },
    languageRoute,
  } = useAppSettingsData();

  const {
    'customerInfo/myOrders/noOrders': noOrdersLabel,
    'orderHistoryPage/dateString': dateLabel,
    'orderHistoryPage/approveOrder': approveOrderLabel,
    'orderHistoryPage/needsToBeApproved': needsToBeApprovedLabel,
  } = useTranslationData();

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const currentPage = useCurrentPage<ActiveOrdersPageModel>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);

  const { customerId, isApprover } = useUserStateData();
  const url = `/api/${
    languageRoute || 'en'
  }/order/GetActiveOrders?customerId=${customerId}`;
  const [orders, setOrders] = useState<OrderDetailsModel[]>();

  const fetchData = async (loadMoreUrl?: string) => {
    if (customerId) {
      let endpoint =
        loadMoreUrl || `${url}&pageIndex=${pageIndex}&pageSize=${pageSize}`;
      const data: OrderInfoViewModel = await GetOrders(
        currentPage.channelId,
        endpoint,
        setIsLoading
      );

      if (data) {
        setOrders(data.activeOrders);
        setTotalCount(data.activeOrdersCount);
      }
    }
  };

  async function handleChangePage(value: any) {
    setPageIndex(value);

    const newUrl = `${url}&pageIndex=${value}&pageSize=${pageSize}`;

    fetchData(newUrl);
  }

  const handleChangePageSize = (value: any) => {
    setPageSize(value);
    const newUrl = `${url}&pageIndex=${pageIndex}&pageSize=${value}`;
    fetchData(newUrl);
  };

  const handleApproveOrder = async (externalOrderID: string) => {
    setIsApproving(true);

    const res = await ApproveOrder(
      currentPage.channelId,
      languageRoute,
      externalOrderID
    );

    if (res.status === 200 && orders) {
      const orderHistoryCopy = [...orders];
      let order = orderHistoryCopy.find(
        (x: any) => x.externalOrderID === externalOrderID
      );

      if (order) {
        order.orderStatus = res.orderStatus;
        setOrders(orderHistoryCopy);
      }
    }

    setIsApproving(false);
  };

  useEffect(() => {
    fetchData();
  }, [currentPage.channelId]);

  return (
    <main>
      {orders && !!orders.length ? (
        <>
          <OrderListing
            heading={currentPage.pageTitle}
            totalCount={totalCount}
            introText={currentPage.pageIntroText}
            changePage={handleChangePage}
            pageSize={pageSize}
            pageIndex={pageIndex}
            changePageSize={handleChangePageSize}
          >
            {orders.map((order: OrderDetailsModel) => {
              const needsToBeApproved =
                isApprover && order.orderStatus === 'Init';

              const title = (
                <div>
                  <div>
                    <StyledAccountKexLink
                      href={`${orderDetailsPage}?orderId=${order.externalOrderID}`}
                    >
                      {order.externalOrderID}
                    </StyledAccountKexLink>
                    {` - ${dateLabel}: ${getFormattedDate(order.orderDate)}`}
                    {needsToBeApproved && (
                      <ApproveText>{` - ${needsToBeApprovedLabel}`}</ApproveText>
                    )}
                  </div>
                </div>
              );

              return (
                <div key={order.orderId}>
                  <Accordion title={title}>
                    {needsToBeApproved && handleApproveOrder && (
                      <ApproveOrderButton
                        onClick={() =>
                          handleApproveOrder(order.externalOrderID)
                        }
                      >
                        {isApproving ? (
                          <StyledLoadingCircle isLoading={isApproving} />
                        ) : (
                          <>{approveOrderLabel}</>
                        )}
                      </ApproveOrderButton>
                    )}
                    <OrderRowListing order={order} />
                  </Accordion>
                  <MaxDivider />
                </div>
              );
            })}
          </OrderListing>
        </>
      ) : !isLoading ? (
        <div>{noOrdersLabel}</div>
      ) : (
        <DotsWrapper>
          <Dots />
        </DotsWrapper>
      )}
    </main>
  );
}

const ApproveText = styled('span', {
  fontWeight: '$fw700',
  fontStyle: 'italic',
});

const StyledLoadingCircle = styled(LoadingCircle, {
  w: 9,
  my: -1,
});

const ApproveOrderButton = styled('button', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  h: 'auto',
  color: '$black',
  p: 2,
  my: 3,
  fs: 10,
  fontWeight: '$fw300',
  transitionDuration: '$200',
  minH: 6,
  '@mediaMinMedium': {
    minH: 8,
  },
});

const PaginationStyle = {
  margin: { xy: '15px' },
  padding: { xy: '15px' },
  backgroundColor: '#F5F5F5',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const MaxDivider = styled(Divider, {
  w: '100%',
  h: 0.25,
  opacity: 0.5,
  m: 0,
});

const StyledAccountKexLink = styled(KexLink, {
  fs: 8,
  fontWeight: '$fw700',
  lineHeight: '28px',
});

const DotsWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  pb: 9,
});

export default ActiveOrdersPage;
